import { EmptyList, Table, TableSkeleton } from '@src/components';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@src/contexts';
import { useEffect, useRef } from 'react';
import { RenderColumns } from './columns';
import { Checkbox } from 'antd';
import _ from 'lodash';
import { useLogsList } from './hooks';
import { LogsFilter } from './components';
import { useDisplayListLogic, useLocalStorageValue } from '@src/hooks';

export const LogsList = () => {
    const { t } = useTranslation();
    const { setBreadcrumbs } = usePageTitle();
    const refHasData = useRef(false);
    const url = new URL(window.location.href);

    useEffect(() => {
        setBreadcrumbs([{ title: t('pages.users.title') }]);
    }, []);

    const storageKey = 'logsHiddenColumns';
    const [hiddenColumns, setHiddenColumns] = useLocalStorageValue<string[]>(
        storageKey,
        []
    );

    const {
        data,
        page,
        pageSize,
        showLocalTime,
        isLoading,
        isFetching,
        openFilter,
        filters,
        searchText,
        isError,
        sortBy,
        sortOrder,
        handleSearchChange,
        handleChangePagination,
        handleRemoveAllFilters,
        handleFilterChange,
        setOpenFilter,
        setShowLocalTime,
        handleActionClick,
        handleSortChange,
    } = useLogsList();
    const columns = RenderColumns({
        t,
        sorterState: { sortBy, sortOrder, onSortChange: handleSortChange },
        showLocalTime,
        hiddenColumns,
        setHiddenColumns,
        onActionClick: handleActionClick,
    });

    const { showTable, showEmptyState } = useDisplayListLogic({
        data: data?.data.items ?? [],
        originalData: [],
        isLoading,
        isFetching,
        isError,
        filters,
        searchText,
    });

    useEffect(() => {
        if (
            !refHasData.current &&
            ((data?.data?.items && data.data.items.length > 0) ||
                url.searchParams.size)
        )
            refHasData.current = true;
    }, [data?.data?.items]);

    const totalBaseColumns =
        columns.filter((c) => c.key !== 'actions').length +
        hiddenColumns.length;
    const allHidden = hiddenColumns.length >= totalBaseColumns;

    return (
        <>
            {isLoading && <TableSkeleton />}

            {showTable && (
                <Table
                    title={
                        <div className="flex gap-2 items-center">
                            <span>
                                {`You have ${data?.data.pagination.total ?? 0} ${t('pages.logs.title')}`}
                            </span>
                        </div>
                    }
                    searchPlaceholder={t('general.searchMinChars')}
                    scroll={{ x: 1 }}
                    actions={
                        <div className="flex flex-col md:flex-row justify-end items-center w-full">
                            <Checkbox
                                title="Local time zone"
                                className="whitespace-nowrap"
                                type="checkbox"
                                checked={showLocalTime}
                                onChange={(e) =>
                                    setShowLocalTime(e.target.checked)
                                }
                            >
                                Local time zone
                            </Checkbox>
                        </div>
                    }
                    headerClassName="md:flex-row-reverse"
                    loading={isLoading || isFetching}
                    columns={columns}
                    dataSource={data?.data.items || []}
                    onPageChange={handleChangePagination}
                    paginationProps={{
                        total: data?.data.pagination.total ?? 0,
                        current: page,
                        pageSize: pageSize,
                    }}
                    onSearch={(e) => handleSearchChange(e)}
                    activeFilter={true}
                    filterOpen={true}
                    filters={filters}
                    onClearFilters={handleRemoveAllFilters}
                    setFilterOpen={() => setOpenFilter(true)}
                    allColumnsHidden={allHidden}
                    onShowAllColumns={() => setHiddenColumns([])}
                />
            )}

            {showEmptyState && (
                <EmptyList
                    message={t('general.emptyListMessage', {
                        name: t('pages.logs.title'),
                    })}
                />
            )}
            {openFilter && (
                <LogsFilter
                    initialFormData={filters}
                    onChange={handleFilterChange}
                    onClose={() => setOpenFilter(false)}
                />
            )}
        </>
    );
};
